import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { getTeachersBySchoolId, requestTeacherWhatsappInvite } from "../services/APIClient";
import "./TeacherWhatsAppInviteModal.css";

const DELIVERY_METHODS = {
  email: "email",
  savedMobile: "savedMobile",
  newMobile: "newMobile"
};

const BACKEND_DELIVERY_METHODS = {
  [DELIVERY_METHODS.email]: "alpaca_lead_email",
  [DELIVERY_METHODS.savedMobile]: "saved_mobile",
  [DELIVERY_METHODS.newMobile]: "new_mobile"
};

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseLookup = (value) => normaliseText(value).toLowerCase();

const normaliseDeliveryMethod = (value) => {
  const method = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");

  if (["email", "school-email", "schoolemail"].includes(method)) {
    return DELIVERY_METHODS.email;
  }

  if (["mobile", "saved-mobile", "savedmobile", "phone", "saved-phone"].includes(method)) {
    return DELIVERY_METHODS.savedMobile;
  }

  if (["new-mobile", "newmobile", "new-phone", "other-mobile"].includes(method)) {
    return DELIVERY_METHODS.newMobile;
  }

  return "";
};

const normalisePrefill = (prefill) => ({
  whatsappGroupName: normaliseText(
    prefill?.whatsappGroupName || prefill?.whatsapp_group_name
  ),
  schoolName: normaliseText(prefill?.schoolName || prefill?.school_name),
  deliveryMethod: normaliseDeliveryMethod(prefill?.deliveryMethod || prefill?.delivery_method),
  mobileNumber: normaliseText(
    prefill?.mobileNumber || prefill?.mobile_number || prefill?.phoneNumber
  ),
  email: normaliseText(prefill?.email || prefill?.schoolEmail || prefill?.school_email)
});

const normaliseTeacher = (teacher) => ({
  ...teacher,
  id: teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId || teacher?.teacher_id,
  name: teacher?.name || teacher?.fullName || teacher?.displayName || "Unnamed teacher",
  email: normaliseText(teacher?.email || teacher?.teacherEmail || teacher?.teacher_email),
  mobileNumber: normaliseText(
    teacher?.mobileNumber ||
    teacher?.mobile_number ||
    teacher?.phoneNumber ||
    teacher?.phone_number ||
    teacher?.contactNumber ||
    teacher?.contact_number ||
    teacher?.mobile ||
    teacher?.phone
  ),
  roles: [
    ...(Array.isArray(teacher?.additionalRoles) ? teacher.additionalRoles : []),
    ...(Array.isArray(teacher?.roles) ? teacher.roles : []),
    teacher?.role,
    teacher?.roleLabel
  ].filter(Boolean)
});

const isAlpacaLead = (teacher) =>
  teacher.roles.some((role) => normaliseLookup(role) === "alpaca lead");

const getDeliveryLabel = (method) => {
  if (method === DELIVERY_METHODS.email) {
    return "Send to ALPACA Lead email";
  }

  if (method === DELIVERY_METHODS.savedMobile) {
    return "Send to saved mobile";
  }

  return "Use a new mobile number";
};

const getDestination = (method, prefillContext, newMobileNumber) => {
  if (method === DELIVERY_METHODS.email) {
    return prefillContext.email;
  }

  if (method === DELIVERY_METHODS.savedMobile) {
    return prefillContext.mobileNumber;
  }

  return normaliseText(newMobileNumber);
};

const getInviteReference = (response) => response?.reference || response?.data?.reference || "";

const getInviteStatus = (response) => response?.status || response?.data?.status || "queued";

const isInviteSent = (invite) => getInviteStatus(invite) === "sent";

function TeacherWhatsAppInviteModal({
  show,
  handleClose,
  schoolid,
  prefill = null,
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("delivery");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [newMobileNumber, setNewMobileNumber] = useState("");
  const [sentInvite, setSentInvite] = useState(null);
  const [alpacaLead, setAlpacaLead] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const deliveryContext = useMemo(
    () => ({
      ...prefillContext,
      email: alpacaLead?.email || prefillContext.email,
      mobileNumber: alpacaLead?.mobileNumber || prefillContext.mobileNumber
    }),
    [alpacaLead, prefillContext]
  );
  const selectedDestination = getDestination(deliveryMethod, deliveryContext, newMobileNumber);
  const canSend = Boolean(deliveryMethod && selectedDestination);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("delivery");
    setDeliveryMethod(prefillContext.deliveryMethod);
    setNewMobileNumber(
      prefillContext.deliveryMethod === DELIVERY_METHODS.newMobile
        ? prefillContext.mobileNumber
        : ""
    );
    setSentInvite(null);
    setAlpacaLead(null);
    setIsSubmitting(false);
    setError("");

    if (!schoolid) {
      return;
    }

    let isCurrentRequest = true;

    getTeachersBySchoolId(schoolid)
      .then((teachersResponse) => {
        if (!isCurrentRequest) {
          return;
        }

        const teachers = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((teacher) => teacher.id);
        const lead = teachers.find(isAlpacaLead);
        setAlpacaLead(lead || null);
      })
      .catch((loadError) => {
        console.error("Error loading WhatsApp invite contact details:", loadError);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [show, schoolid, prefillContext]);

  const selectDeliveryMethod = (method) => {
    setDeliveryMethod(method);
    setError("");

    if (method !== DELIVERY_METHODS.newMobile) {
      setStage("delivery");
    }
  };

  const handleSendInvite = async (methodOverride = deliveryMethod) => {
    const destination = getDestination(methodOverride, deliveryContext, newMobileNumber);

    if (!methodOverride) {
      setError("Choose how to receive the invite link.");
      return;
    }

    if (!destination) {
      setError(
        deliveryMethod === DELIVERY_METHODS.newMobile
          ? "Enter a mobile number."
          : "Choose a delivery option with contact details."
      );
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await requestTeacherWhatsappInvite({
        deliveryMethod: BACKEND_DELIVERY_METHODS[methodOverride],
        destination,
        source: {
          workflow: "send-whatsapp-group-invite"
        }
      });

      setSentInvite({
        ...response,
        deliveryMethod: methodOverride,
        status: getInviteStatus(response),
        reference: getInviteReference(response),
        destination: response?.destination || destination,
        deliveryChannel: response?.deliveryChannel,
        pocWhatsappLink: response?.pocWhatsappLink
      });
      setStage("success");
    } catch (inviteError) {
      console.error("Error requesting WhatsApp invite:", inviteError);
      setError("The WhatsApp invite request could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewMobileContinue = () => {
    setDeliveryMethod(DELIVERY_METHODS.newMobile);
    handleSendInvite(DELIVERY_METHODS.newMobile);
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: sentInvite?.reference || "",
        message: "WhatsApp invite request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const returnToTeacherDashboard = () => {
    if (embedded) {
      handleDone();
      return;
    }

    if (onReturnToTeacherDashboard) {
      onReturnToTeacherDashboard();
      return;
    }

    handleClose();
  };

  const renderDeliveryOption = (method, detail, disabled = false) => {
    const selected = deliveryMethod === method;

    return (
      <button
        type="button"
        className={`whatsapp-invite-method${selected ? " selected" : ""}`}
        onClick={() => selectDeliveryMethod(method)}
        disabled={disabled}
        aria-pressed={selected}
      >
        <span className="whatsapp-invite-radio" aria-hidden="true" />
        <span className="whatsapp-invite-method-copy">
          <strong>{getDeliveryLabel(method)}</strong>
          <span>{detail}</span>
        </span>
        {selected && <span className="whatsapp-invite-selected-label">Selected</span>}
      </button>
    );
  };

  const renderDelivery = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Get WhatsApp Invite</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="whatsapp-invite-intro">
          Choose where to send your group invite link.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="whatsapp-invite-methods" role="group" aria-label="Delivery method">
          {renderDeliveryOption(
            DELIVERY_METHODS.email,
            deliveryContext.email || "ALPACA Lead email not available",
            !deliveryContext.email
          )}
          {renderDeliveryOption(
            DELIVERY_METHODS.savedMobile,
            deliveryContext.mobileNumber || "ALPACA Lead mobile not available",
            !deliveryContext.mobileNumber
          )}
          {renderDeliveryOption(
            DELIVERY_METHODS.newMobile,
            newMobileNumber || "Enter a mobile number for this invite"
          )}
        </div>

        {deliveryMethod === DELIVERY_METHODS.newMobile && (
          <Button
            className="whatsapp-invite-new-mobile-button"
            variant="outline-primary"
            onClick={() => setStage("newMobile")}
          >
            Enter mobile number
          </Button>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="whatsapp-invite-footer-note">
          {canSend ? `${getDeliveryLabel(deliveryMethod)} selected.` : "Choose a delivery method first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canSend || isSubmitting} onClick={() => handleSendInvite()}>
          {isSubmitting ? "Sending..." : "Request invite link"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderNewMobile = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Use New Mobile Number</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="whatsapp-invite-warning">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>New mobile number</strong>
            <span>This number is used for this invite link only.</span>
          </span>
        </Alert>

        <Form.Group controlId="teacherWhatsAppInviteNewMobile">
          <Form.Label>Mobile number</Form.Label>
          <Form.Control
            type="tel"
            value={newMobileNumber}
            onChange={(event) => {
              setNewMobileNumber(event.target.value);
              setError("");
            }}
            placeholder="Enter mobile number"
            aria-label="Mobile number"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="whatsapp-invite-footer-note">
          {newMobileNumber.trim() ? "Ready to send invite." : "Enter a mobile number."}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("delivery")}>
          Back
        </Button>
        <Button
          variant="primary"
          disabled={!newMobileNumber.trim() || isSubmitting}
          onClick={handleNewMobileContinue}
        >
          {isSubmitting ? "Sending..." : "Request invite link"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => {
    const sent = isInviteSent(sentInvite);
    const reference = sentInvite?.reference;

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">{sent ? "Invite Link Sent" : "Invite Request Queued"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success" className="whatsapp-invite-success">
            <FaCheckCircle aria-hidden="true" />
            <span>
              <strong>
                {sent
                  ? `The WhatsApp invite link has been sent to ${sentInvite?.destination}.`
                  : `ALPACA has received the request to send the WhatsApp invite to ${sentInvite?.destination}.`}
              </strong>
              {reference && <span>Reference: {reference}.</span>}
            </span>
          </Alert>
          <div className="whatsapp-invite-summary">
            <strong>What happens next</strong>
            <span>
              {sent
                ? "Use the invite link from the message to join the WhatsApp group."
                : "ALPACA will handle the request and follow up if anything else is needed."}
            </span>
            {sentInvite?.pocWhatsappLink && (
              <span className="whatsapp-invite-row">
                <span>POC link</span>
                <span>{sentInvite.pocWhatsappLink}</span>
              </span>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span className="whatsapp-invite-footer-note">{sent ? "Invite link sent." : "Invite request received."}</span>
          {!embedded && (
            <Button variant="outline-secondary" onClick={returnToTeacherDashboard}>
              Return to Teacher Dashboard
            </Button>
          )}
          <Button variant="primary" onClick={handleDone}>
            Close
          </Button>
        </Modal.Footer>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-whatsapp-invite-modal"
    >
      {stage === "delivery" && renderDelivery()}
      {stage === "newMobile" && renderNewMobile()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherWhatsAppInviteModal;
